
import { Component, Ref, Vue } from 'vue-property-decorator'
import ProofreadCurriculumUnits from '@/components/templates/manage/ProofreadCurriculumUnits.vue'
import { GradeCode } from '@/const/GradeCode'
import { SubjectCode } from '@/const/SubjectCode'
import { SelectItem } from '@/models/SelectItem'
import { ProofreadUnit } from '@/models/manage/ProofreadList'
import { CurriculumUnitsApi } from '@/api/methods/proofread/CurriculumUnitsApi'

/**
 * 校正画面-単元一覧画面
 */
@Component({
  components: {
    ProofreadCurriculumUnits,
  },
})
export default class AcademyCurriculumUnits extends Vue {
  /** テンプレート情報 */
  @Ref()
  private proofreadCurriculumUnits!: ProofreadCurriculumUnits

  /** 読み込み完了フラグ */
  private isLoaded = false

  /** 学年コード初期値 */
  private defaultGradeCode!: string

  /** 教科コード初期値 */
  private defaultSubjectCode!: string

  /** 単元一覧 */
  private unitList: ProofreadUnit[] = []

  /**
   * 学年一覧
   *
   * @returns 学年一覧
   */
  private get gradeList(): SelectItem[] {
    // 学年一覧の成形
    return GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })
  }

  /**
   * 教科一覧
   *
   * @returns 教科一覧
   */
  private get subjectList(): SelectItem[] {
    // 教科一覧の成形
    return SubjectCode.LIST.map((subject) => {
      return {
        value: subject.code,
        label: subject.label,
      }
    })
  }

  /**
   * デフォルト学年設定
   */
  private setDefaultGradeCode(): void {
    // クエリから学年コードを取得
    let queryGradeCode = this.$route.query.grade

    if (!queryGradeCode || typeof queryGradeCode !== 'string') {
      // 未指定時、指定不正時は空文字列に置換
      queryGradeCode = ''
    }

    // 定義情報から有効な学年情報を取得
    const grade = GradeCode.findByCode(queryGradeCode)

    // 有効な学年情報をデフォルト値に設定(無効/未指定の場合はリスト先頭を使用)
    this.defaultGradeCode = grade?.code || this.gradeList[0].value
  }

  /**
   * デフォルト教科設定
   */
  private setDefaultSubjectCode(): void {
    // クエリから教科コードを取得
    let querySubjectCode = this.$route.query.subject

    if (!querySubjectCode || typeof querySubjectCode !== 'string') {
      // 未指定時、指定不正時は空文字列に置換
      querySubjectCode = ''
    }

    // 定義情報から有効な教科情報を取得
    const subject = SubjectCode.findByCode(querySubjectCode)

    // 有効な学年情報をデフォルト値に設定(無効/未指定の場合はリスト先頭を使用)
    this.defaultSubjectCode = subject?.code || this.subjectList[0].value
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 学年初期値設定
    this.setDefaultGradeCode()

    // 教科初期値設定
    this.setDefaultSubjectCode()

    // 単元一覧取得
    await this.loadCurriculumUnits(this.defaultGradeCode, this.defaultSubjectCode)

    this.isLoaded = true

    this.$loading.complete()
  }

  /**
   * 条件変更時のイベント処理
   */
  private async eventChangeFilter(): Promise<void> {
    this.$loading.start()

    // 条件の選択状況でブロック
    if (this.proofreadCurriculumUnits.gradeCode && this.proofreadCurriculumUnits.subjectCode) {
      // 単元一覧を再取得
      await this.loadCurriculumUnits(this.proofreadCurriculumUnits.gradeCode, this.proofreadCurriculumUnits.subjectCode)
    }

    this.$loading.complete()
  }

  /**
   * 単元一覧取得
   *
   * @param gradeCode 学年コード
   * @param subjectCode 教科コード
   */
  private async loadCurriculumUnits(gradeCode: string, subjectCode: string): Promise<void> {
    // API実行
    const curriculumUnits = await CurriculumUnitsApi.getCurriculumUnits(gradeCode, subjectCode)
    this.unitList = curriculumUnits.map((unit) => {
      return {
        id: unit.id,
        name: unit.name,
        code: unit.code,
      }
    })
  }

  /**
   * 単元選択時の処理
   *
   * @param id 単元ID
   */
  private async eventSelectUnit(id: number): Promise<void> {
    await this.$router.push(`/academy/proofread/${id}`)
  }
}
